import { useState } from 'react'
import { motion } from 'framer-motion'
import { ArrowRight, Loader2 } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'

interface WelcomeStepProps {
  onNext: () => void
}

export default function WelcomeStep({ onNext }: WelcomeStepProps) {
  const [email, setEmail] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const { startRegistration, isLoading, error, clearError } = useAuthStore()

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  const canContinue = isValidEmail && acceptedTerms && !isLoading

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canContinue) return

    try {
      await startRegistration(email)
      onNext()
    } catch {
      // Error is handled by the store
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-8"
    >
      {/* Minimal header - no step indicators, the left panel shows context */}
      <div className="space-y-3">
        <h2 className="text-3xl font-extralight tracking-tight text-white">
          Create your identity
        </h2>
        <p className="text-white/40 font-light">
          Enter the email you want associated with your Λ account.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Email input - Apple-style minimal */}
        <div className="space-y-2">
          <label htmlFor="email" className="block text-xs font-medium text-white/50 uppercase tracking-widest">
            Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              if (error) clearError()
            }}
            placeholder="you@example.com"
            required
            autoFocus
            disabled={isLoading}
            className="
              w-full px-0 py-4
              bg-transparent border-0 border-b border-white/10
              text-white text-lg font-light placeholder-white/20
              focus:outline-none focus:border-white/30
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors duration-300
            "
          />
          {/* Validation indicator - subtle line under input */}
          {email && (
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              className={`h-px origin-left ${isValidEmail ? 'bg-white/40' : 'bg-red-500/40'}`}
            />
          )}
        </div>

        {/* Error message */}
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm text-red-400/80 font-light"
          >
            {error}
          </motion.p>
        )}

        {/* Terms acceptance - minimal checkbox */}
        <label className="flex items-start gap-4 cursor-pointer group">
          <div className="relative mt-1">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              disabled={isLoading}
              className="sr-only peer"
            />
            <div className="
              w-5 h-5 border border-white/20 rounded-sm
              peer-checked:bg-white peer-checked:border-white
              peer-focus:ring-1 peer-focus:ring-white/30
              transition-all duration-200
            " />
            {acceptedTerms && (
              <motion.svg
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute inset-0 w-5 h-5 text-black p-1"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={3}
              >
                <path d="M5 12l5 5L20 7" />
              </motion.svg>
            )}
          </div>
          <span className="text-sm text-white/40 leading-relaxed group-hover:text-white/60 transition-colors duration-300">
            I agree to the{' '}
            <a href="/terms" target="_blank" className="text-white/60 hover:text-white underline underline-offset-4 decoration-white/20 hover:decoration-white/40 transition-colors duration-300">
              Terms
            </a>{' '}
            and{' '}
            <a href="/privacy" target="_blank" className="text-white/60 hover:text-white underline underline-offset-4 decoration-white/20 hover:decoration-white/40 transition-colors duration-300">
              Privacy Policy
            </a>
          </span>
        </label>

        {/* Submit button - minimal, bold */}
        <button
          type="submit"
          disabled={!canContinue}
          className="
            w-full py-4 mt-4
            bg-white text-black text-sm font-medium tracking-wide
            rounded-none
            disabled:opacity-20 disabled:cursor-not-allowed
            hover:bg-white/90
            active:bg-white/80
            transition-all duration-200
            flex items-center justify-center gap-3
          "
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <span>Continue</span>
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </form>

      {/* Sign in link - barely visible until hover */}
      <p className="text-center text-xs text-white/20">
        Already have an account?{' '}
        <a href="/" className="text-white/40 hover:text-white transition-colors duration-300">
          Sign in
        </a>
      </p>
    </motion.div>
  )
}
